import React, { useState } from "react";
import useTranslator from "../../../hooks/useTranslator";
import { getCurrentDate } from "../../../helpers/Utils";
import DateRangeFilter from "../../../components/shared/fields/DateRangeFilter";
import DashboardSection from "../../dashboard/components/DashboardSection";
import "../../dashboard/components/dashboard-sections.css";
import { ScaleIconSvg } from "../../../svgFiles/ScaleIconSvg";
import { CycleIconSvg } from "../../../svgFiles/CycleIconSvg";
import { TrendingUpIconSvg } from "../../../svgFiles/TrendingUpIconSvg";
import { SnowflakeIconSvg } from "../../../svgFiles/SnowflakeIconSvg";
const BrokerageDashboard = () => {
  const { _t } = useTranslator();  
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [, setStartDate] = useState(currentDate);
  const [, setEndDate] = useState(currentDate);

  const handleApplyRange = ({ startDate: start, endDate: end }) => {
    setStartDate(start);
    setEndDate(end);
    setDate(start);
  };

  // MetricBox component
  const MetricBox = ({ label, value, unit }) => (
    <div className="db-evap-box">
      <div className="db-evap-label">{label}</div>
      <div className="db-evap-value">
        {value ?? "0"}
        {unit && <span className="db-metric-unit"> {unit}</span>}
      </div>
    </div>
  );

  // Running Totals Items
  const runningTotalsItems = [
    { label: _t("total_paid", "Total Paid"), value: 0, unit: " KG" },
    { label: _t("total_received", "Total Received"), value: 0, unit: " KG" },
  ];

  // Processing Items - with nested structure
  const processingItems = [
    {
      title: _t("prime", "Prime"),
      total: 0,
      unit: " KG",
      details: [
        { label: "Cheeks", value: 0, unit: " KG" },
        { label: "Strips", value: 0, unit: " KG" },
      ]
    },
    {
      title: _t("second", "Second"),
      total: 0,
      unit: " KG",
      details: [
        { label: "Cheeks", value: 0, unit: " KG" },
        { label: "Strips", value: 0, unit: " KG" },
      ]
    },
    {
      title: _t("pieces", "Pieces"),
      total: 0,
      unit: " KG",
      details: null
    },
    {
      title: _t("pasting", "Pasting"),
      total: 0,
      unit: " KG",
      details: null
    },
  ];

  // Sorting Items
  const sortingTopItems = [
    { label: _t("total_sorted", "Total Sorted"), value: 0, unit: " KG" },
    { label: _t("rejected", "Rejected"), value: 0, unit: " KG" },
  ];

  const sortingBottomItems = [
    { label: _t("prime_cheeks_boxes_10", "Prime Cheeks boxes(10)"), value: 0, category: "Prime" },
    { label: _t("prime_cheeks_boxes_20", "Prime Cheeks boxes(20)"), value: 0, category: "Prime" },
    { label: _t("prime_strips_boxes_20", "Prime Strips boxes(20)"), value: 0, category: "Prime" },
    { label: _t("second_cheeks_boxes_10", "Second Cheeks boxes(10)"), value: 0, category: "Second" },
    { label: _t("second_cheeks_boxes_20", "Second Cheeks boxes(20)"), value: 0, category: "Second" },
    { label: _t("second_strips_boxes_20", "Second Strips boxes(20)"), value: 0, category: "Second" },
    { label: _t("pieces_boxes_20", "Pieces boxes(20)"), value: 0, category: "Pieces" },
    { label: _t("pasting_boxes", "Pasting boxes"), value: 0, category: "Pasting" },
  ];

  // Day/Night Separation Items
  const separationItems = [
    {
      title: _t("day_separation", "Day Separation"),
      items: [
        { label: _t("strips_kg", "Strips KG"), value: 0, unit: " KG" },
        { label: _t("cheeks_kg", "Cheeks KG"), value: 0, unit: " KG" },
        { label: _t("number_of_people_separating", "Number of People Separating"), value: 0, unit: "" },
      ]
    },
    {
      title: _t("night_separation", "Night Separation"),
      items: [
        { label: _t("strips_kg", "Strips KG"), value: 0, unit: " KG" },
        { label: _t("cheeks_kg", "Cheeks KG"), value: 0, unit: " KG" },
        { label: _t("number_of_people_separating", "Number of People Separating"), value: 0, unit: "" },
      ]
    },
  ];

  return (
    <div>
      {/* ── Toolbar with Date Filter ──────────────────────────── */}
      <div className="receiving_dashboard_toolbar align-items-center">
        <h5 className="receiving_dashboard_toolbar_title m-0">
          {_t("brokerage_overview", "Brokerage Overview")}
        </h5>
        <div className="receiving_filter_group">          
          <DateRangeFilter setDate={setDate} onApplyRange={handleApplyRange} triggerId="brokerage-date-range-filter" />          
        </div>
      </div>

      <div>

        {/* ── Running Totals Section ── */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <TrendingUpIconSvg size={18} color="#4ade80" />
              {_t("running_totals_session", "Running totals (session)")}
            </span>
          }
        >
          <div className="db-evap-row p-0">
            {runningTotalsItems.map((item, i) => (
              <MetricBox key={i} label={item.label} value={item.value} unit={item.unit} />
            ))}
          </div>
        </DashboardSection>

        {/* ── Processing Section ── */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <CycleIconSvg size={18} color="#60a5fa" />
              {_t("processing_overview", "Processing Overview")}
            </span>
          }
        >
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px", padding: "0 16px" }}>
            {processingItems.map((item, i) => (
              <div key={i} style={{ border: "1px solid #f0f1f3", borderRadius: "8px", padding: "16px", backgroundColor: "#fff" }}>
                <div style={{ fontSize: "13px", fontWeight: "600", color: "#1d1f2c", marginBottom: "12px" }}>
                  {item.title}
                </div>
                <div style={{ fontSize: "20px", fontWeight: "700", color: "#1d1f2c", marginBottom: item.details ? "12px" : "0" }}>
                  {item.total}{item.unit}
                </div>
                {item.details && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px", paddingTop: "12px", borderTop: "1px solid #f0f1f3" }}>
                    {item.details.map((detail, j) => (
                      <div key={j} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: "12px", color: "#667085" }}>
                          {detail.label}
                        </span>
                        <span style={{ fontSize: "14px", fontWeight: "600", color: "#1d1f2c" }}>
                          {detail.value}{detail.unit || ""}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </DashboardSection>

        

        {/* ── Sorting Section ── */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <ScaleIconSvg size={18} color="#4ade80" />
              {_t("sorting", "Sorting")}
            </span>
          }
        >
          <div className="db-evap-row p-0 mb-3">
            {sortingTopItems.map((item, i) => (
              <MetricBox key={i} label={item.label} value={item.value} unit={item.unit} />
            ))}
          </div>

          <div className="db-avg-card p-0">
            {sortingBottomItems.reduce((acc, item, idx) => {
              const lastCategory = acc.length > 0 ? acc[acc.length - 1].category : null;
              if (item.category !== lastCategory) {
                acc.push({ type: "header", category: item.category, key: `header-${item.category}` });
              }
              acc.push({ ...item, type: "row", key: idx });
              return acc;
            }, []).map((element, i) => (
              element.type === "header" ? (
                <div key={element.key} className="db-avg-header" style={i > 0 ? { borderTop: "1px solid #f0f1f3" } : {}}>
                  <span className="db-avg-header-title">{element.category}</span>
                </div>
              ) : (
                <div key={element.key} className="db-avg-row">
                  <span>{_t(element.label, element.label)}</span>
                  <span className="db-avg-val">{element.value}</span>
                </div>
              )
            ))}
          </div>
        </DashboardSection>
        {/* ── Day/Night Separation Section ── */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <SnowflakeIconSvg size={18} color="#fb923c" />
              {_t("day_night_separation", "Day/Night Separation")}
            </span>
          }
        >
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px", padding: "0 16px" }}>
            {separationItems.map((section, i) => (
              <div key={i} style={{ border: "1px solid #f0f1f3", borderRadius: "8px", padding: "16px", backgroundColor: "#fff" }}>
                <div style={{ fontSize: "13px", fontWeight: "600", color: "#1d1f2c", marginBottom: "16px" }}>
                  {section.title}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                  {section.items.map((item, j) => (
                    <div key={j} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: "12px", color: "#667085" }}>
                        {item.label}
                      </span>
                      <span style={{ fontSize: "14px", fontWeight: "600", color: "#1d1f2c" }}>
                        {item.value}{item.unit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DashboardSection>
      </div>
    </div>
  );
};

export default BrokerageDashboard;
